import countries from 'i18n-iso-countries';
import moment from 'moment';
import React, { useState } from 'react';
import { generatePath, NavLink, useParams } from 'react-router-dom';
import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import ContactTypeIcon from 'flow/components/contact/ContactTypeIcon';
import CallHistoryContextMenu from 'flow/components/context-menus/CallHistoryContextMenu';
import ContextMenu from 'flow/components/ContextMenu';
import CallRecordTypeIcon from 'flow/components/custom-components/CallRecordTypeIcon';
import Icon from 'flow/components/ui-components/Icon';
import NumberWrapper from 'flow/components/ui-components/NumberWrapper';
import { useModal } from 'flow/contexts/ModalProvider';
import { getExtensionFromContactKey } from 'flow/selectors/selectors';
import { getFormattedDateAndTime } from 'flow/utils';
import {
    getContactFromCallRecord,
    getCallRecordDisplayName,
    getContactFullName,
    showBankidVerification,
} from 'flow/utils/call-record-utils';
import {
    callRecordIsMissed,
    getCountryCodeFromCallRecord,
} from 'flow/utils/call-records-utils';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { twMerge } from 'tailwind-merge';
import {
    selectContact,
    selectOwnExtensionKey,
    selectUserCountryCode,
} from 'flow/selectors/ExtensionSelectors';
import useAppContext from 'flow/hooks/useAppContext';
import PbxCallRecordDetailItems from 'flow/components/ui-components/PbxCallRecordDetailItems';
import { CallRecordKey, ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import Button from 'flow/components/ui-components/Button';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TypedMap } from 'flow/types/TypedMap';
import { CallRecord } from 'flow/types/CallRecord';
import { selectCallRecordFromKey } from 'flow/selectors/call-records-selectors';
import { useTranscribeRecordedCallWithProgress } from 'flow/hooks/api/useTranscribeRecordedCallWithProgress';
import { AICoworkerCallDetailEvals } from 'flow/hooks/api/useAiCoworkerCallDetailEvals';
import { getMoreOptionsLabel } from 'flow/utils/a11y-utils';

const RESOLVED_ICON = '✅';
const UNRESOLVED_ICON = '❌';
const SATISFIED_ICON = '😀';
const UNSATISFIED_ICON = '☹️';

interface Props {
    callRecordKey: CallRecordKey;
    isCompactView?: boolean;
    displayQueueNameAndDate: boolean;
    evaluation?: AICoworkerCallDetailEvals;
}

const AICoworkerCallRecordItem = ({
    callRecordKey,
    isCompactView,
    displayQueueNameAndDate,
    evaluation,
}: Props) => {
    const { openModal } = useModal();
    const { i18next } = useAppContext();
    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const contact = useAppSelector((state) =>
        selectContact(state, extensionKey),
    );
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );

    const [contextMenuIsActive, setContextMenuIsActive] = useState(false);

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const ownCountryCode = useAppSelector(selectUserCountryCode);

    const extensionToCall = useAppSelector((state) =>
        getExtensionFromContactKey(
            state,
            callRecord?.getIn(['contact', 'key']),
        ),
    );

    const phoneNumber = callRecord?.getIn(['number', 'number']);

    const isOwnExtension = extensionToCall
        ? extensionToCall?.get('key') === ownExtensionKey
        : false;

    const {
        data: transcription,
        isFetching,
        transcribe,
    } = useTranscribeRecordedCallWithProgress(
        callRecord?.getIn(['recordedCall', 'key']),
        callRecord?.getIn(['recordedCall', 'isPersonalRecording']),
    );

    const openTranscriptionModal = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        event.preventDefault();
        openModal('transcriptionDetail', {
            callRecord,
        });
    };

    const onTranscribeClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        event.preventDefault();
        transcribe();
    };

    const hasCompletedTranscription = !isFetching && !!transcription;

    return (
        <NavLink
            className="group flex gap-1 rounded-sm p-2 whitespace-nowrap hover:bg-gray-100"
            to={{
                pathname: generatePath(
                    routes['pbx.aicoworker.extensionKey.calls.callRecordKey'],
                    {
                        callRecordKey,
                        extensionKey,
                    },
                ),
                state: {
                    from: window.location.pathname,
                },
            }}
        >
            <div
                key={callRecordKey}
                className={twMerge(
                    'group flex flex-auto gap-1 rounded-sm p-2 whitespace-nowrap',
                    contextMenuIsActive ? 'bg-gray-100' : 'hover:bg-gray-100',
                )}
            >
                <div className="flex flex-row py-2">
                    <CallRecordTypeIcon callRecordKey={callRecord.get('key')} />
                </div>

                <div className="flex w-full flex-col gap-2">
                    <div className="flex min-h-9 items-center gap-x-2">
                        <div className="flex w-full items-center justify-between">
                            <div
                                className={twMerge(
                                    'flex gap-2',
                                    callRecordIsMissed(callRecord)
                                        ? 'text-red'
                                        : 'text-gray-800',
                                )}
                            >
                                <CallRecordTitle callRecord={callRecord} />
                                <ContactTypeIcon
                                    contact={callRecord.get('contact')}
                                />
                            </div>

                            <div
                                className={twMerge(
                                    'items-center gap-1 text-xs whitespace-nowrap text-gray-500',
                                    contextMenuIsActive
                                        ? 'hidden'
                                        : 'group-focus-visible-within:hidden flex group-hover:hidden',
                                )}
                            >
                                {showBankidVerification(callRecord) && (
                                    <Icon iconKey="gppGood" size="1rem" />
                                )}
                                {displayQueueNameAndDate
                                    ? getFormattedDateAndTime(
                                          callRecord.get('connected'),
                                          i18next,
                                      )
                                    : moment(
                                          callRecord.get('connected'),
                                      ).format('HH:mm')}
                            </div>

                            <div
                                className={twMerge(
                                    'gap-x-2',
                                    contextMenuIsActive
                                        ? 'flex'
                                        : 'sr-only-until-hover-or-focus flex',
                                )}
                            >
                                {callRecord.getIn(['contact', 'key']) &&
                                    !isOwnExtension && (
                                        <ContextMenu
                                            triggerLabel={getMoreOptionsLabel(
                                                i18next,
                                                getCallRecordDisplayName(
                                                    callRecord,
                                                    ownCountryCode,
                                                ),
                                            )}
                                            setActive={setContextMenuIsActive}
                                            lockScroll={{
                                                scroll: document.querySelector(
                                                    '#main-scrollable-div',
                                                ),
                                                wrapper: document.querySelector(
                                                    '#main-scrollable-div',
                                                ),
                                            }}
                                        >
                                            <CallHistoryContextMenu
                                                callRecordKey={callRecord.get(
                                                    'key',
                                                )}
                                                onClickCreateContact={() =>
                                                    openModal('createContact', {
                                                        contact:
                                                            getContactFromCallRecord(
                                                                callRecord,
                                                            ),
                                                        countryCode:
                                                            ownCountryCode?.toUpperCase(),
                                                    })
                                                }
                                            />
                                        </ContextMenu>
                                    )}
                                {phoneNumber && !isOwnExtension ? (
                                    <CallAdaptiveButton
                                        phoneNumber={phoneNumber}
                                        transferContact={contact}
                                        activeBg={true}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="line-clamp-1 items-center pb-2 text-sm break-all text-ellipsis text-gray-500">
                        <CallRecordDetail
                            callRecord={callRecord}
                            isCompactView={isCompactView}
                        />
                    </div>
                    {displayQueueNameAndDate ? (
                        <span className="text-sm text-gray-500">
                            {callRecord.getIn(['queue', 'description'])}
                        </span>
                    ) : null}

                    <div
                        className={twMerge(
                            'flex w-full items-center gap-x-2',
                            isCompactView ? 'justify-between' : '',
                        )}
                    >
                        <div className="flex items-center gap-x-2">
                            {evaluation && (
                                <div className="flex items-center gap-1.5">
                                    {evaluation.resolved !== undefined && (
                                        <span className="flex h-7 w-7 items-center justify-center rounded bg-gray-100 group-hover:bg-gray-200">
                                            {evaluation.resolved
                                                ? RESOLVED_ICON
                                                : UNRESOLVED_ICON}
                                        </span>
                                    )}
                                    {evaluation.satisfied !== undefined && (
                                        <span className="flex h-7 w-7 items-center justify-center rounded bg-gray-100 group-hover:bg-gray-200">
                                            {evaluation.satisfied
                                                ? SATISFIED_ICON
                                                : UNSATISFIED_ICON}
                                        </span>
                                    )}
                                </div>
                            )}
                            <PbxCallRecordDetailItems
                                callRecord={callRecord}
                                contextMenuIsActive={contextMenuIsActive}
                                hideInQueueDuration={true}
                            />
                        </div>
                        {!hasCompletedTranscription &&
                        callRecord.getIn([
                            'recordedCall',
                            'isTranscribable',
                        ]) ? (
                            <Button
                                onClick={(event) => onTranscribeClick(event)}
                                color="gray"
                                iconKey="text_snippet"
                                className="!min-w-0"
                                size="small"
                                disabled={isFetching}
                            >
                                {i18next.t('ai.transcribe.transcribe')}
                            </Button>
                        ) : null}
                        {hasCompletedTranscription &&
                            callRecord.getIn([
                                'recordedCall',
                                'isTranscribable',
                            ]) && (
                                <Button
                                    onClick={(event) =>
                                        openTranscriptionModal(event)
                                    }
                                    color="gray"
                                    className="!min-w-0"
                                    size="small"
                                >
                                    {i18next.t(
                                        'ai.transcript.show_full_transcript',
                                    )}
                                </Button>
                            )}
                    </div>
                </div>
            </div>
        </NavLink>
    );
};

interface CallRecordTitleProps {
    callRecord: TypedMap<CallRecord>;
}

const CallRecordTitle = ({ callRecord }: CallRecordTitleProps) => {
    const userCountryCode = useAppSelector(selectUserCountryCode);
    const name = getContactFullName(callRecord);

    if (name) {
        return name;
    }

    if (callRecord.getIn(['number', 'type']) === 'technical_number') {
        return null;
    }

    return (
        <div className="truncate overflow-hidden">
            <NumberWrapper>
                {callRecord.getIn(['contact', 'type']) === 'global'
                    ? callRecord.getIn(['contact', 'companyName'])
                    : prettyFormatE164Number(
                          callRecord.getIn(['number', 'number']),
                          userCountryCode,
                      )}
            </NumberWrapper>
        </div>
    );
};

interface CallRecordDetailProps {
    callRecord: TypedMap<CallRecord>;
    isCompactView?: boolean;
}

const CallRecordDetail = ({
    callRecord,
    isCompactView = false,
}: CallRecordDetailProps) => {
    const userCountryCode = useAppSelector(selectUserCountryCode);
    if (callRecord.getIn(['number', 'type']) === 'technical_number') {
        return null;
    }

    if (!getContactFullName(callRecord)) {
        return countries.getName(
            getCountryCodeFromCallRecord(callRecord),
            moment.locale(),
        );
    }

    return (
        <>
            <NumberWrapper>
                {prettyFormatE164Number(
                    callRecord.getIn(['number', 'number']),
                    userCountryCode,
                )}
            </NumberWrapper>
            {!isCompactView ? (
                <span className="ms-2">
                    {callRecord.getIn(['contact', 'companyName'])}
                </span>
            ) : null}
        </>
    );
};

export default AICoworkerCallRecordItem;
